import { typedObjectEntries } from '@gozoki/tools'
import { createFileRoute, Outlet, type ToOptions } from '@tanstack/react-router'
import { Calendar, CalendarCheck2, LayoutGrid, Users, type LucideIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect } from 'react'
import z from 'zod/v3'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { DateRangePicker } from '~/components/ui/date-range-picker'
import { hrPeriodOptions, periodDateRanges, periods } from '~/components/new/app-ui/period-select'
import { StoreCombobox } from '~/components/new/app-ui/store-combobox'
import { StoreMultiSelect } from '~/components/routes/hr/planning/dashboard/store-multi-select'
import { Title } from '~/components/new/app-ui/title'
import { EXPORT_WEEK_PRESETS } from '~/utils/dates/export-presets'
import { resolveAttendanceRange } from '~/utils/dates/attendance-range'
import { PlanningAbsenceCalendar } from '~/components/routes/hr/planning/absence-calendar'
import { PlanningAttendance } from '~/components/routes/hr/planning/attendance'
import { PlanningCollaborator } from '~/components/routes/hr/planning/collaborators'
import { PlanningDashboard } from '~/components/routes/hr/planning/dashboard'
import { PlanningCalendar } from '~/components/routes/hr/planning/planning'
import { ExportAttendanceButton } from '~/components/routes/hr/planning/attendance/export-attendance-button'
import { ExportPlanningButton } from '~/components/routes/hr/planning/planning/export-planning-button'
import { PlanningSettingsDialog } from '~/components/routes/hr/planning/planning/planning-settings'
import { SelectionPeriodBadge } from '~/components/routes/network-dashboard/selection-period-badge'
import { useSidebar } from '~/components/ui/sidebar'
import { withRoutePermissions } from '~/utils/rbac'

const tabValues = [
  'dashboard',
  'calendar',
  'collaborators',
  'attendance',
  'absence-calendar',
] as const
type TabValue = (typeof tabValues)[number]

export const Route = createFileRoute('/_authenticated/hr/planning/$tab')({
  staticData: {
    permission: ['read', 'hr-planning'],
  },
  component: RouteComponent,
  params: {
    parse: z.object({ tab: z.enum(tabValues) }).parse,
  },
  validateSearch: z.object({
    // Global search params
    storeId: z.number().optional(),
    // Multi-sélection de l'onglet Tableau de bord — état séparé de storeId (aucune synchro).
    storeIds: z.array(z.number()).optional(),

    // Dashboard specific search params
    period: z.enum(periods).optional(),

    // Planning specific search params
    weekNumber: z.number().optional(),
    year: z.number().optional(),

    // Pointage : plage de dates pilotant à la fois le tableau et l'export (ISO 'YYYY-MM-DD').
    dateStart: z.string().optional(),
    dateEnd: z.string().optional(),
  }),
})
withRoutePermissions(Route)

const tabOptions = {
  dashboard: {
    label: 'Tableau de bord',
    icon: LayoutGrid,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'dashboard' } },
    component: <PlanningDashboard />,
  },
  calendar: {
    label: 'Planning',
    icon: Calendar,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'calendar' } },
    component: <PlanningCalendar />,
  },
  collaborators: {
    label: 'Personnel',
    icon: Users,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'collaborators' } },
    component: <PlanningCollaborator />,
  },
  attendance: {
    label: 'Pointage',
    icon: CalendarCheck2,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'attendance' } },
    component: <PlanningAttendance />,
  },
  'absence-calendar': {
    label: 'Calendrier Absences',
    icon: Calendar,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'absence-calendar' } },
    component: <PlanningAbsenceCalendar />,
  },
} as const satisfies Record<
  TabValue,
  { toOptions: ToOptions; component: React.ReactNode; label: string; icon: LucideIcon }
>

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { storeId, period = '7-days', dateStart, dateEnd } = Route.useSearch()
  const { tab } = Route.useParams()

  // Plage pointage partagée (tableau + export) ; défaut = semaine courante quand l'URL est vide.
  const attendanceRange = resolveAttendanceRange(dateStart, dateEnd)

  // The HR dashboard mockup (Figma 7007:11548) is laid out for a 1440px viewport with the
  // sidebar collapsed to its icon rail — collapse it while in this section, restore on leave.
  // Mount/unmount only: re-running on sidebar state changes would re-collapse the sidebar
  // whenever the user expands it manually.
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar()
  useEffect(() => {
    const wasOpen = sidebarOpen
    setSidebarOpen(false)
    return () => setSidebarOpen(wasOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const TabComponent = tabOptions[tab].component

  const periodLabel = hrPeriodOptions.find((o) => o.value === period)?.label ?? ''
  const { start, end } = periodDateRanges[period]()

  return (
    <>
      {/* Densité maquette (Figma 7007:11548, viewport 1440×780) : header 52px + tabs 46px,
          le tableau de bord doit tenir sans scroll — paddings resserrés vs défauts AppHeader/AppContainer. */}
      <AppHeader className="px-5 py-1 mb-1">
        <div className="flex items-center gap-2">
          <Title className="text-foreground text-[28px] font-extrabold leading-[normal] font-brand">
            Dashboard RH
          </Title>
          {tab === 'dashboard' && (
            <SelectionPeriodBadge startDate={start} endDate={end} label={periodLabel} />
          )}
        </div>
      </AppHeader>

      <AppContainer className="p-3 gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AppTabs
            tabs={typedObjectEntries(tabOptions).map(([key, value]) => ({
              label: value.label,
              icon: value.icon,
              value: key,
            }))}
            value={tab}
            onTabChange={(v) =>
              navigate({
                ...tabOptions[v].toOptions,
                search: (prev) => ({ ...prev }),
              })
            }
          />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {tab === 'dashboard' ? (
              <StoreMultiSelect />
            ) : (
              <StoreCombobox
                withAll
                onChange={(v) =>
                  navigate({ search: (prev) => ({ ...prev, storeId: v ?? undefined }) })
                }
                value={storeId ?? null}
              />
            )}
            {tab === 'calendar' && <ExportPlanningButton />}
            {tab === 'attendance' && (
              <>
                <DateRangePicker
                  value={{ from: attendanceRange.from, to: attendanceRange.to }}
                  onChange={(newRange) =>
                    navigate({
                      search: (prev) => ({
                        ...prev,
                        dateStart: newRange.from
                          ? (DateTime.fromJSDate(newRange.from).toISODate() ?? undefined)
                          : undefined,
                        dateEnd: DateTime.fromJSDate(newRange.to).toISODate() ?? undefined,
                      }),
                    })
                  }
                  presets={EXPORT_WEEK_PRESETS}
                />
                <ExportAttendanceButton
                  storeId={storeId}
                  dateStart={attendanceRange.dateStart}
                  dateEnd={attendanceRange.dateEnd}
                />
              </>
            )}
            {tab === 'calendar' && <PlanningSettingsDialog />}
          </div>
        </div>

        {TabComponent}
      </AppContainer>

      {/* Panneau latéral d'édition du collaborateur focus (route imbriquée /hr/planning/$tab/$collaboratorId) */}
      <Outlet />
    </>
  )
}
